import { useState, useEffect } from "react";
import { useAllMcqs } from "@/hooks/useAllMcqs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Activity, 
  AlertTriangle, 
  CheckCircle, 
  TrendingUp, 
  TrendingDown,
  RefreshCw,
  FileText,
  Target,
  Users,
  Calendar
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface ContentHealth {
  overallScore: number;
  totalContent: number;
  categories: {
    qualityIssues: number;
    accuracyIssues: number;
    difficultyMismatches: number;
    outdatedContent: number;
  };
  trends: {
    period: string;
    qualityTrend: number;
    accuracyTrend: number;
    engagementTrend: number;
  };
  recommendations: {
    priority: "high" | "medium" | "low";
    action: string;
    description: string;
    affectedContent: number;
  }[];
  contentBreakdown: {
    type: string;
    total: number;
    healthy: number;
    issues: number;
    score: number;
  }[];
}

export default function ContentHealthMonitor() {
  const { toast } = useToast();
  const { data: allMcqs } = useAllMcqs();
  const [healthData, setHealthData] = useState<ContentHealth | null>(null);
  const [timeRange, setTimeRange] = useState("7d");
  const [isLoading, setIsLoading] = useState(false);

  const fetchHealthData = async () => {
    setIsLoading(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('ai-content-health', {
        body: {
          timeRange: timeRange
        }
      });

      if (error) throw error;
      
      setHealthData(data.health);
      
    } catch (error) {
      console.error('Error fetching health data:', error);
      toast({
        title: "Failed to Load Data",
        description: "Could not fetch content health data. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchHealthData();
  }, [timeRange]);

  // Mock data for demonstration - use actual MCQ count
  useEffect(() => {
    if (!healthData && allMcqs) {
      const totalMcqs = allMcqs.length;
      setHealthData({
        overallScore: 78,
        totalContent: totalMcqs,
        categories: {
          qualityIssues: 34,
          accuracyIssues: 12,
          difficultyMismatches: 28,
          outdatedContent: 45
        },
        trends: {
          period: "Last 7 days",
          qualityTrend: 5.2,
          accuracyTrend: -2.1,
          engagementTrend: 8.7
        },
        recommendations: [
          {
            priority: "high",
            action: "Update Outdated Content",
            description: "45 pieces of content haven't been reviewed in over 6 months",
            affectedContent: 45
          },
          {
            priority: "medium",
            action: "Fix Quality Issues",
            description: "34 items have quality scores below 70%",
            affectedContent: 34
          },
          {
            priority: "medium",
            action: "Review Difficulty Mismatches",
            description: "28 items have difficulty levels that don't match user performance",
            affectedContent: 28
          },
          {
            priority: "low",
            action: "Validate Medical Accuracy",
            description: "12 items flagged for potential medical accuracy issues",
            affectedContent: 12
          }
        ],
        contentBreakdown: [
          { type: "MCQs", total: totalMcqs, healthy: Math.floor(totalMcqs * 0.85), issues: Math.floor(totalMcqs * 0.15), score: 85 },
          { type: "Notes", total: 234, healthy: 189, issues: 45, score: 81 },
          { type: "Explanations", total: 121, healthy: 98, issues: 23, score: 81 }
        ]
      });
    }
  }, [healthData, allMcqs]);

  const getHealthColor = (score: number) => {
    if (score >= 90) return "text-success";
    if (score >= 75) return "text-primary";
    if (score >= 60) return "text-warning";
    return "text-destructive";
  };

  const getHealthStatus = (score: number) => {
    if (score >= 90) return { label: "Excellent", color: "success" };
    if (score >= 75) return { label: "Good", color: "default" };
    if (score >= 60) return { label: "Fair", color: "warning" };
    return { label: "Poor", color: "destructive" };
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high": return "destructive";
      case "medium": return "warning";
      case "low": return "default";
      default: return "secondary";
    }
  };

  const getTrendIcon = (trend: number) => {
    if (trend > 0) return <TrendingUp className="h-4 w-4 text-success" />;
    if (trend < 0) return <TrendingDown className="h-4 w-4 text-destructive" />;
    return <Activity className="h-4 w-4 text-muted-foreground" />;
  };

  const formatTrend = (trend: number) => {
    const sign = trend > 0 ? "+" : "";
    return `${sign}${trend.toFixed(1)}%`;
  };

  if (!healthData) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium">Content Health Monitor</h3>
          <p className="text-sm text-muted-foreground">
            Overall health tracking and monitoring of your content library
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchHealthData} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Overall Health
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className={`text-2xl font-bold ${getHealthColor(healthData.overallScore)}`}>
                {healthData.overallScore}%
              </div>
              <Badge variant={getHealthStatus(healthData.overallScore).color as any}>
                {getHealthStatus(healthData.overallScore).label}
              </Badge>
              <Progress value={healthData.overallScore} className="h-2" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Total Content
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="text-2xl font-bold">{healthData.totalContent.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">Pieces of content</p>
              <div className="text-xs">
                {healthData.totalContent - Object.values(healthData.categories).reduce((a, b) => a + b, 0)} healthy items
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Issues Found
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="text-2xl font-bold text-warning">
                {Object.values(healthData.categories).reduce((a, b) => a + b, 0)}
              </div>
              <p className="text-xs text-muted-foreground">Items need attention</p>
              <div className="text-xs text-destructive">
                {Math.round((Object.values(healthData.categories).reduce((a, b) => a + b, 0) / healthData.totalContent) * 100)}% of total
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Quality Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center gap-1">
                {getTrendIcon(healthData.trends.qualityTrend)}
                <span className={healthData.trends.qualityTrend > 0 ? "text-success" : "text-destructive"}>
                  {formatTrend(healthData.trends.qualityTrend)}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">{healthData.trends.period}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Issue Categories
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm">Quality Issues</span>
                <Badge variant="secondary">{healthData.categories.qualityIssues}</Badge>
              </div>
              <Progress value={(healthData.categories.qualityIssues / healthData.totalContent) * 100} className="h-2" />
            </div>

            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm">Accuracy Issues</span>
                <Badge variant="destructive">{healthData.categories.accuracyIssues}</Badge>
              </div>
              <Progress value={(healthData.categories.accuracyIssues / healthData.totalContent) * 100} className="h-2" />
            </div>

            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm">Difficulty Mismatches</span>
                <Badge variant="secondary">{healthData.categories.difficultyMismatches}</Badge>
              </div>
              <Progress value={(healthData.categories.difficultyMismatches / healthData.totalContent) * 100} className="h-2" />
            </div>

            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm">Outdated Content</span>
                <Badge variant="secondary">{healthData.categories.outdatedContent}</Badge>
              </div>
              <Progress value={(healthData.categories.outdatedContent / healthData.totalContent) * 100} className="h-2" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-4 w-4" />
              Content Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {healthData.contentBreakdown.map((item, index) => (
              <div key={index} className="p-3 border rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium">{item.type}</span>
                  <Badge variant={getHealthStatus(item.score).color as any}>
                    {item.score}%
                  </Badge>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs text-muted-foreground">
                  <div>Total: {item.total}</div>
                  <div className="text-success">Healthy: {item.healthy}</div>
                  <div className="text-warning">Issues: {item.issues}</div>
                </div>
                <Progress value={(item.healthy / item.total) * 100} className="h-1 mt-2" />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            Action Recommendations
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {healthData.recommendations.map((rec, index) => (
            <div key={index} className="flex items-start gap-3 p-3 border rounded-lg">
              <Badge variant={getPriorityColor(rec.priority) as any} className="mt-0.5">
                {rec.priority}
              </Badge>
              <div className="flex-1">
                <div className="flex justify-between items-start mb-1">
                  <h4 className="font-medium text-sm">{rec.action}</h4>
                  <span className="text-xs text-muted-foreground">
                    {rec.affectedContent} items
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">{rec.description}</p>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Quality Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              {getTrendIcon(healthData.trends.qualityTrend)}
              <span className={healthData.trends.qualityTrend > 0 ? "text-success" : "text-destructive"}>
                {formatTrend(healthData.trends.qualityTrend)}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {healthData.trends.period}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              Accuracy Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              {getTrendIcon(healthData.trends.accuracyTrend)}
              <span className={healthData.trends.accuracyTrend > 0 ? "text-success" : "text-destructive"}>
                {formatTrend(healthData.trends.accuracyTrend)}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {healthData.trends.period}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Users className="h-4 w-4" />
              Engagement Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              {getTrendIcon(healthData.trends.engagementTrend)}
              <span className={healthData.trends.engagementTrend > 0 ? "text-success" : "text-destructive"}>
                {formatTrend(healthData.trends.engagementTrend)}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {healthData.trends.period}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}